import { computed, type Ref } from "vue";
import { useQuery } from "@tanstack/vue-query";
import Unipile from "@/services/unipile/unipile";
import type { Conversation } from "@/dtos/Unipile.dto";
import type { WhatsAppConversationsParams } from "./types";

function isRef<T>(param: Ref<T> | T): param is Ref<T> {
  return typeof param === "object" && param !== null && "value" in param;
}

/**
 * Composable for fetching WhatsApp conversations using TanStack Query
 */
export function useWhatsAppConversations(
  params: Ref<WhatsAppConversationsParams | null> | WhatsAppConversationsParams | null
) {
  const queryKey = computed(() => {
    const p = isRef(params) ? params.value : params;
    if (!p) return ["whatsapp-conversations", "disabled"];
    return ["whatsapp-conversations", p.contactId, p.accountId, p.limit || 50];
  });

  const enabled = computed(() => {
    const p = isRef(params) ? params.value : params;
    return (
      p !== null &&
      p !== undefined &&
      !!p.contactId &&
      !!p.accountId &&
      p.contactId !== "" &&
      p.accountId !== ""
    );
  });

  return useQuery({
    queryKey,
    queryFn: async () => {
      const p = isRef(params) ? params.value : params;
      if (!p) throw new Error("Parameters not ready");

      const response = await Unipile.getAttendeeChats("contact", {
        contact_id: p.contactId,
        account_id: p.accountId,
        limit: p.limit || 50,
      });

      const conversations = Array.isArray(response.data.data?.items)
        ? response.data.data.items
        : [];

      // Transform conversations to match component needs
      const transformedConversations: Conversation[] = conversations.map((conv: any) => {
        const chatData = conv;
        const name = conv.name || chatData.title || p.contactName || "Unknown";
        return {
          ...conv,
          id: conv.id,
          channel: "whatsapp",
          participants: conv.participants || [],
          contactName: name,
          title: name,
          lastMessage: chatData.last_message?.text || "",
          lastMessageAt: conv.timestamp || new Date().toISOString(),
          unreadCount: conv.unread_count || 0,
          lastMessageIsSender: chatData.last_message?.is_sender || false,
          lastMessageSeen: chatData.last_message?.seen || false,
          lastMessageDelivered: chatData.last_message?.delivered || false,
        };
      });

      return transformedConversations;
    },
    enabled,
    staleTime: 30 * 1000, // 30 seconds
    gcTime: 5 * 60 * 1000, // 5 minutes
  });
}
